<template lang="pug">
  el-form(label-position='top' ref='userForm' :model='user' :rules='userRules')
    el-form-item(label="First name" prop="firstName")
      el-input(v-model.trim="user.firstName")

    el-form-item(label="Surname" prop="lastName")
      el-input(v-model.trim="user.lastName")

    el-form-item(label="Phone number" prop="phoneNum")
      el-input.phone-num(v-model.trim="user.phoneNum")
        flag(slot="prepend" v-if='parsedPhoneNum && parsedPhoneNum.country' :iso='parsedPhoneNum.country')
        icon(slot="prepend" v-else name="question" color='#666')

    el-form-item(label="Email address" prop="email")
      el-input(v-model.trim="user.email" :icon="checkEmailLoading ? 'loading' : null")

    el-form-item(label="Job role" prop="jobRole")
      el-input(v-model.trim="user.jobRole")

    el-form-item(label="Account status" prop="isActive")
      el-radio-group(v-model="user.isActive")
        el-radio(:label='true') Active
        el-radio(:label='false') Disable

    el-form-item(v-if='showCompanyAdmin' label="Company admin" prop="isCompanyAdmin")
      el-switch(v-model="user.isCompanyAdmin" on-text="" off-text="")

    template(v-if='showPassword')
      el-form-item(:label="user.generatePassword ? 'Password' : 'Set manual password'")
        el-checkbox(v-model="user.generatePassword") Generate password automatically (will be emailed)

      el-collapse-transition
        .panel(v-if='!user.generatePassword')
          el-form-item(label="Password" prop="password")
            el-input(v-model="user.password" type='password')

          el-form-item(label="Confirm password" prop="confirmPassword")
            el-input(v-model="confirmPassword" type='password')

</template>

<script>
import { mapActions } from "vuex";
import { parse, isValidNumber, format as formatPhone } from "libphonenumber-js";

export default {
  model: {
    prop: "user",
    event: "change"
  },
  props: {
    user: {
      type: Object,
      default: () => ({})
    },
    edit: {
      type: Boolean,
      default: false
    },
    showPassword: {
      type: Boolean,
      default: true
    },
    showCompanyAdmin: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    "user.phoneNum": function watchPhoneNum(value) {
      this.parsedPhoneNum = parse(value);
    }
  },
  data() {
    return {
      checkEmailLoading: false,
      confirmPassword: "",
      initialEmail: null,
      parsedPhoneNum: null,
      userRules: {
        firstName: [
          { required: true, message: "Required field", trigger: "blur" }
        ],
        lastName: [
          { required: true, message: "Required field", trigger: "blur" }
        ],
        phoneNum: [{ validator: this.validatePhoneNum, trigger: "blur" }],
        email: [
          {
            required: true,
            type: "email",
            message: "Please enter a valid email address",
            trigger: "blur"
          },
          { validator: this.validateUniqueEmail, trigger: "blur" }
        ],
        jobRole: [
          { required: true, message: "Required field", trigger: "blur" }
        ],
        password: [{ validator: this.validatePassword, trigger: "change" }],
        confirmPassword: [
          { validator: this.validateConfirmPassword, trigger: "change" }
        ]
      }
    };
  },
  computed: {},
  mounted() {
    if (this.edit) {
      this.initialEmail = this.user.email;
    }
    if (this.user.phoneNum) {
      this.parsedPhoneNum = parse(this.user.phoneNum);
    }
  },
  methods: {
    ...mapActions({
      fetchUsers: "users/fetchUsers"
    }),
    validatePassword(rule, value, callback) {
      if (this.user.generatePassword) {
        return callback();
      }

      if (!value) {
        return callback(new Error("This field is required"));
      }

      const regex = /^(?=.*\d)(?=.*[a-z])[0-9a-zA-Z]{8,}$/;

      if (!regex.test(value)) {
        return callback(
          new Error(
            "Password must be at least 8 characters, at least 1 number and at least 1 lowercase letter."
          )
        );
      }

      return callback();
    },
    validateConfirmPassword(rule, _value, callback) {
      if (!this.showPassword || this.user.generatePassword) {
        return callback();
      }
      if (
        !this.confirmPassword ||
        this.confirmPassword !== this.user.password
      ) {
        return callback(new Error("Passwords don't match"));
      }
      return callback();
    },
    validateUniqueEmail(rule, value, callback) {
      if (!value) {
        callback(new Error("Please input the email"));
        return;
      }
      if (this.edit && this.initialEmail && this.initialEmail === value) {
        callback();
        return;
      }

      this.checkEmailLoading = true;
      this.fetchUsers({
        params: { email: value },
        filterByActiveCompany: true
      }).then(resp => {
        const checkResult = resp.length
          ? new Error("Email already used by another account")
          : undefined;
        this.checkEmailLoading = false;
        callback(checkResult);
      });
    },
    validatePhoneNum(rule, value, callback) {
      if (!value) {
        return callback();
      }

      if (!isValidNumber(value)) {
        return callback(
          new Error(
            "This number does not seem valid. Please make sure to use the international format."
          )
        );
      }

      const parsedPhoneNum = parse(value);
      const formattedNum = formatPhone(parsedPhoneNum, "International");
      this.user.phoneNum = formattedNum;

      return callback();
    },
    validate(...args) {
      return this.$refs.userForm.validate(...args);
    }
  }
};
</script>

<style scoped lang="scss">
.panel {
  padding: 10px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 2px;
}

.phone-num {
  :global(.el-input-group__prepend) {
    width: 40px;
    text-align: center;

    svg {
      position: relative;
      top: 3px;
    }
  }
}
</style>
